import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { AiSummaryQuestion } from '../../questions/AiSummaryQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const AiSummaryRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  flowRunId,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const initialValue = useMemo(() => {
    const raw = existingResponse?.text_value ?? '';
    // If the backend saved the no-data placeholder, treat it as empty so we regenerate
    if (raw === 'No responses available for summary generation.') return '';
    return raw;
  }, [existingResponse]);
  const [textValue, setTextValue] = useState<string>(initialValue);

  const emit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const hasContent = trimmed.length > 0;

      // ai_summary is a no-input type — user doesn't provide input
      onHasUserInputChange?.(false);
      onValidityChange?.(hasContent);

      if (!hasContent) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        text_value: trimmed,
      };

      onDraftChange(payload);
    },
    [onDraftChange, onHasUserInputChange, onValidityChange, questionId],
  );

  useEffect(() => {
    setTextValue(initialValue);
    emit(initialValue);
  }, [emit, initialValue, questionId]);

  const handleTextChange = useCallback(
    (value: string) => {
      setTextValue(value);
      emit(value);
    },
    [emit],
  );

  const handleLoadingStateChange = useCallback(
    (isLoading: boolean) => {
      onExtrasChange?.({ isAiSummaryLoading: isLoading });
    },
    [onExtrasChange],
  );

  return (
    <AiSummaryQuestion
      question={question}
      textValue={textValue}
      onTextChange={handleTextChange}
      onLoadingStateChange={handleLoadingStateChange}
      isSubmitting={isSubmitting}
      flowRunId={flowRunId}
    />
  );
};

registerQuestionRenderer(['ai_summary'], AiSummaryRenderer);
